import { CopilotStudioClient, ConnectionSettings } from '@microsoft/agents-copilotstudio-client';
import { ADAPTIVE_CARD_CONTENT_TYPE, AGENT_DIRECT_CONNECT_URL } from './constants';
import { AgentItem, AgentReply, AgentSend, AgentSuggestedAction } from './types';

// ---------------------------------------------------------------------------
// Copilot Studio ("AI agent") bridge
// ---------------------------------------------------------------------------
// Hands a question off to the published Copilot Studio agent (the same one used
// in Teams) and returns its reply for rendering through the markdown beautifier.
// The client is created lazily with an Entra token from the existing Backstage
// Microsoft sign-in — no second login.

// Runtime flags shared with the chat flow: `agentMode` keeps every message routed
// to the agent once entered; `lastAgentSuggestedActions` surfaces the agent's
// latest suggested-action buttons as chat options.
export const agentRuntime = {
  agentMode: false,
  lastAgentSuggestedActions: [] as string[],
};

let agentClient: CopilotStudioClient | null = null;
let agentConversationStarted = false;
// Bridges an Adaptive Card action (or suggested action) back into the agent flow.
let pendingAgentPayload: AgentSend = "";
export const setAgentPayload = (p: AgentSend) => { pendingAgentPayload = p; };
export const takeAgentPayload = (): AgentSend => { const p = pendingAgentPayload; pendingAgentPayload = ""; return p; };

export const resetAgentConversation = () => {
  agentClient = null;
  agentConversationStarted = false;
  agentRuntime.lastAgentSuggestedActions = [];
  pendingAgentPayload = "";
};

// Drains a streamed Copilot Studio response into ordered text/card items plus
// suggested-action buttons. Handles progressive streaming (typing activities
// carrying partial text) and de-duplicates progressive text updates.
const collectAgentReply = async (gen: AsyncGenerator<any>, onProgress?: (text: string) => void): Promise<AgentReply> => {
  const items: AgentItem[] = [];
  let lastText: { kind: 'text'; text: string } | null = null;
  let lastStreamText = "";
  const suggestedActions: AgentSuggestedAction[] = [];
  const seenActions = new Set<string>();

  for await (const act of gen) {
    const text: string = act?.text ?? "";
    if (act?.type === 'message') {
      if (text) {
        // Collapse progressive updates that extend the current text bubble.
        if (lastText && (text.startsWith(lastText.text) || lastText.text.startsWith(text))) {
          lastText.text = text.length >= lastText.text.length ? text : lastText.text;
        } else {
          lastText = { kind: 'text', text };
          items.push(lastText);
        }
      }
      for (const at of (act?.attachments ?? [])) {
        if (at?.contentType === ADAPTIVE_CARD_CONTENT_TYPE && at?.content) {
          items.push({ kind: 'card', card: at.content });
          lastText = null; // text after a card starts a fresh bubble
        }
      }
      for (const a of (act?.suggestedActions?.actions ?? [])) {
        const title: string = a?.title ?? (typeof a?.value === 'string' ? a.value : '');
        if (!title || seenActions.has(title)) continue;
        seenActions.add(title);
        const value = typeof a?.value === 'string' ? a.value : a?.value != null ? JSON.stringify(a.value) : title;
        suggestedActions.push({ title, value });
      }
    } else if (act?.type === 'typing' && text) {
      lastStreamText = text;
      onProgress?.(text);
    }
  }
  if (items.length === 0 && lastStreamText.trim()) {
    items.push({ kind: 'text', text: lastStreamText.trim() });
  }
  const trimmed = items
    .map(it => (it.kind === 'text' ? { kind: 'text' as const, text: it.text.trim() } : it))
    .filter(it => it.kind !== 'text' || it.text);
  return { items: trimmed, suggestedActions };
};

// Sends `input` to the Copilot Studio agent (starting the conversation on first
// use) and returns its ordered text/card items and suggested actions. A card
// action is sent with its `value` populated so the agent treats it as a card
// submit (required by the Teams "Connect to continue" consent card).
export const askCopilotAgent = async (
  getToken: () => Promise<string | undefined>,
  input: AgentSend,
  onProgress?: (text: string) => void,
): Promise<AgentReply> => {
  try {
    if (!agentClient) {
      const token = await getToken();
      if (!token) throw new Error('No access token for the AI agent.');
      const settings = new ConnectionSettings({ directConnectUrl: AGENT_DIRECT_CONNECT_URL });
      agentClient = new CopilotStudioClient(settings, token);
      agentConversationStarted = false;
    }
    if (!agentConversationStarted) {
      await collectAgentReply(agentClient.startConversationStreaming(false));
      agentConversationStarted = true;
    }
    // Plain object is fine — the client JSON-serializes the activity before sending.
    const activity = typeof input === 'string'
      ? { type: 'message', text: input }
      : { type: 'message', text: input.text ?? '', value: input.value };
    return await collectAgentReply(agentClient.sendActivityStreaming(activity as any), onProgress);
  } catch (e) {
    console.error('Copilot Studio agent request failed:', e);
    resetAgentConversation();
    return { items: [{ kind: 'text', text: "Sorry, I couldn't reach the AI agent right now. Please try again in a moment." }], suggestedActions: [] };
  }
};
